import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { Router, ActivatedRoute } from '@angular/router';
import { APP_ROUTES } from '../../enums/appRoutes.enum';
import { Observable, Subject, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { FormStateService, UserProfileService } from 'src/app/core/services';
import { ICurrentPhaseInfo } from 'src/app/core/interfaces';
import { IHeaderButton } from '../../interfaces';

@Component({
  standalone: false,
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  pageTitle = 'Home';
  pageSubtitle = '';
  pageButtons: IHeaderButton[] = [];
  isFormValid = false;
  showBackButton = false;
  private isValidSSN = false;

  /**
   * MVP-18 Stage 3 — pipes the cached profile's `currentPhase` snapshot
   * into the global `<app-phase-renewal-banner>` so it can render itself
   * only when renewal is actually due.
   */
  currentPhase$: Observable<ICurrentPhaseInfo | null> = this.userProfileService
    .getProfileObservable()
    .pipe(map(profile => profile?.currentPhase ?? null));

  constructor(
    private router: Router,
    private formStateService: FormStateService,
    private activatedRoute: ActivatedRoute,
    private userProfileService: UserProfileService
  ) {}

  ngOnInit(): void {
    this.handleRouteAndNavigation();
    this.subscribeToFormStateChanges();
    this.subscribeToHeaderStateChanges();
  }
  /**
   * @description Handles the route and navigation events
   * @returns {void} void
   */
  private handleRouteAndNavigation(): void {
    this.updateHeader(this.router.url);
    this.router.events.subscribe(event => {
      if (!event) return;
      if (event instanceof NavigationEnd) {
        this.updateHeader(event.url);
      }
    });
  }
  /**
   * @description Subscribes to form state changes
   * @returns {void} void
   */
  private subscribeToFormStateChanges(): void {
    this.formStateService
      .getFormState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.isFormValid = state.isValid;
        this.updateButtonState();
      });
  }

  /**
   * @description Subscribes to header state changes
   * @returns {void} void
   */
  private subscribeToHeaderStateChanges(): void {
    this.formStateService
      .getHeaderState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (state) {
          this.pageTitle = state.title;
          this.pageSubtitle = state.subtitle || '';
          if (state.reportState) {
            this.isValidSSN = state.reportState.isValidSSN || false;
            this.updateExportButtonState();
          }
        }
      });
  }

  /**
   * @description Updates the button state based on form validation and user role
   * @returns {void} void
   */
  private updateButtonState(): void {
    if (this.router.url.includes(APP_ROUTES.NEW_TEST)) {
      const runTestButton = this.pageButtons.find(button => button.id === 'run-test');
      if (runTestButton) {
        runTestButton.disabled = !this.isFormValid || this.userProfileService.isViewer();
      }
    }
  }

  /**
   * @description Updates the header title and buttons dynamically based on the route.
   * @param {string} url - The current route URL.
   */
  private updateHeader(url: string): void {
    this.showBackButton = false;
    this.pageSubtitle = '';
    if (url.includes(APP_ROUTES.HOME)) {
      this.pageTitle = 'shared.components.header.home';
      this.pageButtons = [
        {
          id: 'new-test',
          label: 'shared.actions.buttons.create_test',
          action: this.createTest.bind(this),
          icon: 'assets/icons/add.svg',
          buttonType: 'primary',
        },
      ];
    } else if (url.includes(APP_ROUTES.NEW_TEST)) {
      this.pageTitle = 'shared.components.header.new_test';
      this.showBackButton = true;
      this.pageButtons = [
        {
          id: 'save-test',
          label: 'shared.actions.buttons.save',
          action: this.saveTest.bind(this),
          icon: 'assets/icons/save.svg',
          buttonType: 'secondary',
        },
        {
          id: 'run-test',
          label: 'shared.actions.buttons.calculate',
          action: this.runTest.bind(this),
          icon: 'assets/icons/check.svg',
          disabled: !this.isFormValid || this.userProfileService.isViewer(),
          buttonType: 'primary',
        },
      ];
      this.updateButtonState();
    } else if (url.includes(`${APP_ROUTES.TESTS}/${APP_ROUTES.PATIENT_REPORT}`)) {
      // Handle patient report routes first (more specific)
      this.pageTitle = 'shared.components.header.patient_report';
      this.showBackButton = true;
      this.pageButtons = [
        {
          id: 'update-test',
          label: 'shared.actions.buttons.edit',
          action: this.updateTest.bind(this),
          icon: 'assets/icons/edit.svg',
          buttonType: 'secondary',
        },
        {
          id: 'print-report',
          label: 'shared.actions.buttons.print',
          action: this.printReport.bind(this),
          icon: 'assets/icons/print.svg',
          buttonType: 'secondary',
        },
        {
          id: 'export-report',
          label: 'Export',
          action: this.exportReport.bind(this),
          icon: 'assets/icons/export.svg',
          buttonType: 'secondary',
          disabled: !this.isValidSSN,
        },
      ];
    } else if (url.includes(APP_ROUTES.TESTS_LIST) || url === `/${APP_ROUTES.TESTS}` || url === APP_ROUTES.TESTS) {
      // Handle both /tests/list and intermediate /tests URL
      this.pageTitle = 'shared.components.header.test_results';
      this.pageButtons = [
        {
          id: 'new-test',
          label: 'shared.actions.buttons.create_test',
          action: this.createTest.bind(this),
          icon: 'assets/icons/add.svg',
          buttonType: 'primary',
        },
      ];
    } else if (url.includes(APP_ROUTES.SETTINGS)) {
      this.pageTitle = 'shared.components.header.settings';
      this.pageButtons = [
        {
          id: 'new-test',
          label: 'shared.actions.buttons.create_test',
          action: this.createTest.bind(this),
          icon: 'assets/icons/add.svg',
          buttonType: 'primary',
        },
      ];
    }
  }

  /**
   * @description Handles creating a new test.
   */
  private createTest(): void {
    this.router.navigate([APP_ROUTES.NEW_TEST]);
  }

  /**
   * @description Handles running a test.
   * @returns {void} void
   */
  private runTest(): void {
    if (this.isFormValid && !this.userProfileService.isViewer()) {
      this.formStateService.triggerAction('calculate');
    }
  }

  /**
   * @description Handles saving a test.
   */
  private saveTest(): void {
    this.formStateService.triggerAction('save');
  }

  /**
   * @description Handles updating a test - navigates to score form with requestId
   */
  private updateTest(): void {
    // Use Angular's reactive approach to get the current route parameter
    const requestId = this.getCurrentRouteParam('requestId');

    if (requestId) {
      this.router.navigate([`${APP_ROUTES.NEW_TEST}/${requestId}`]);
    } else {
      // Fallback to new test if no requestId is available
      this.router.navigate([APP_ROUTES.NEW_TEST]);
    }
  }

  /**
   * @description Gets a route parameter from the current route tree using Angular's best practices
   * @param {string} paramName - The name of the parameter to retrieve
   * @returns {string | null} The parameter value if found, null otherwise
   */
  private getCurrentRouteParam(paramName: string): string | null {
    // Use Angular's proper route traversal mechanism
    let route: ActivatedRoute | null = this.activatedRoute;

    // Check current route and all child routes
    while (route) {
      if (route.snapshot.params[paramName]) {
        return route.snapshot.params[paramName];
      }
      route = route.firstChild;
    }

    // If not found in children, check parent route
    route = this.activatedRoute.parent;
    while (route) {
      if (route.snapshot.params[paramName]) {
        return route.snapshot.params[paramName];
      }
      route = route.parent;
    }

    return null;
  }

  /**
   * @description Handles printing the report.
   * @returns {void} void
   */
  private printReport(): void {
    this.formStateService.triggerAction('print');
  }

  /**
   * @description Handles exporting the report as PDF.
   * @returns {void} void
   */
  private exportReport(): void {
    this.formStateService.triggerAction('export');
  }

  /**
   * @description Updates the Export button state based on SSN validity
   * @returns {void} void
   */
  private updateExportButtonState(): void {
    const exportButton = this.pageButtons.find(button => button.id === 'export-report');
    if (exportButton) {
      exportButton.disabled = !this.isValidSSN;
    }
  }

  /**
   * @description Navigates back to the test results page.
   */
  goBack(): void {
    this.router.navigate([APP_ROUTES.TESTS_LIST]);
  }

  /**
   * @description Updates the page subtitle
   * @param {string} subtitle - The subtitle to display
   */
  updateSubtitle(subtitle: string): void {
    this.pageSubtitle = subtitle;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
